import { useEffect, useState } from 'react';
import { sanitizeValue } from '../../lib/sanitize';
// local
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { useSort } from '../useSort';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addDeviceModel'
  | 'bulkAddDeviceModels'
  | 'getDeviceModel'
  | 'getDeviceModels'
  | 'updateClientStoreValue'
  | 'updateDeviceModel';

export const useDeviceModels = () => {
  const { error, loading, makeRequest, response } = useFetch();
  const {
    id: operatorId,
    type: operatorType,
    models,
    updateOperatorState,
  } = useOperator();
  const { sortAsc } = useSort<DeviceModel>('name');
  const [action, setAction] = useState<Action>('');
  const [bulkDeviceModels, setBulkDeviceModels] = useState<BulkDeviceModels>();
  const [deviceModel, setDeviceModel] = useState<DeviceModel | null>(null);
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const BASE_URL = `${BASE_URL_MAP[operatorType]}/${operatorId}/DeviceModels`;

  useEffect(() => {
    if (response && !loading) {
      if (action === 'bulkAddDeviceModels') {
        const bulkResponse = mapBulkResponse(response, operatorType);
        setBulkDeviceModels(bulkResponse);
        updateOperatorState('models', [
          ...(models ?? []).concat(bulkResponse.success).sort(sortAsc),
        ]);
      } else if (
        action === 'updateDeviceModel' ||
        action === 'updateClientStoreValue'
      ) {
        const mappedDeviceModel = mapDeviceModel(response, operatorType);
        setDeviceModel(mappedDeviceModel);
        updateOperatorState(
          'models',
          [
            mappedDeviceModel,
            ...(models ?? []).filter((device) => {
              if (operatorType === 'Residential')
                return device.name !== mappedDeviceModel.name;
              else return device.id !== mappedDeviceModel.id;
            }),
          ].sort(sortAsc)
        );
      } else if (action === 'addDeviceModel') {
        const mappedDeviceModel = mapDeviceModel(response, operatorType);
        updateOperatorState(
          'models',
          [mappedDeviceModel, ...(models ?? [])].sort(sortAsc)
        );
      } else if (action === 'getDeviceModels')
        setBulkDeviceModels(
          response.map((device: ApiDeviceModel) =>
            mapDeviceModel(device, operatorType)
          )
        );
    }
  }, [loading, response]);

  useEffect(() => {}, [error, loading]);

  useEffect(() => {
    resetState();
  }, [operatorId]);

  const addDeviceModel = (deviceModel: CreateDeviceModel) => {
    setAction('addDeviceModel');
    const body = mapApiDeviceModel(deviceModel, operatorType);

    makeRequest(BASE_URL, 'post', sanitizeValue(body));
  };

  const bulkAddDeviceModels = (deviceModels: CreateDeviceModel[]) => {
    if (operatorType === 'Hospitality')
      throw 'Bulk Device Models is only available for Residential Operators.';
    setAction('bulkAddDeviceModels');
    setBulkDeviceModels({ success: [], fail: [] });
    const body = deviceModels.map((deviceModel) =>
      mapApiDeviceModel(deviceModel, operatorType)
    );

    makeRequest(`${BASE_URL}/BulkAdd`, 'post', sanitizeValue(body));
  };

  const clearBulkAdds = () => setBulkDeviceModels({ success: [], fail: [] });

  const getDeviceModels = () => {
    setAction('getDeviceModels');
    makeRequest(BASE_URL, 'get');
  };

  const getDeviceModel = (deviceModelId: string) => {
    setAction('getDeviceModels');
    makeRequest(`${BASE_URL}/${deviceModelId}`, 'get');
  };

  const updateClientStoreValue = (deviceModel: DeviceModel) => {
    setAction('updateClientStoreValue');
    const deviceModelId =
      operatorType === 'Residential' ? deviceModel.name : deviceModel.id;
    const body = deviceModel.clientStoreValue
      ? deviceModel.clientStoreValue
      : null;

    makeRequest(`${BASE_URL}/${deviceModelId}/ClientStoreValue`, 'put', body);
  };

  const updateDeviceModel = (deviceModel: DeviceModel) => {
    setAction('updateDeviceModel');
    const deviceModelId =
      operatorType === 'Residential' ? deviceModel.name : deviceModel.id;
    const body = mapApiDeviceModel(deviceModel, operatorType);

    makeRequest(`${BASE_URL}/${deviceModelId}`, 'put', sanitizeValue(body));
  };

  const resetState = () => {
    setAction('');
    setBulkDeviceModels({ success: [], fail: [] });
    setDeviceModels([]);
    setDeviceModel(null);
  };

  return {
    addDeviceModel,
    bulkDeviceModels,
    bulkAddDeviceModels,
    clearBulkAdds,
    getDeviceModel,
    getDeviceModels,
    updateClientStoreValue,
    updateDeviceModel,
    loading,
    deviceModel,
    deviceModels,
  };
};

const mapApiDeviceModel = (
  deviceModel: CreateDeviceModel,
  operatorType: OperatorType
): ApiHosCreateDeviceModel | ApiResCreateDeviceModel => {
  if (operatorType === 'Residential') return mapApiResDeviceModel(deviceModel);
  return mapApiHosDeviceModel(deviceModel);
};

const mapApiHosDeviceModel = (
  model: CreateDeviceModel | DeviceModel
): ApiHosCreateDeviceModel => {
  if (model.manufacturer === undefined)
    throw 'Manufacturer is required when creating or updating a Hospitality Device Model';
  if (model.deviceTypeID === undefined)
    throw 'DeviceTypeID is required when creating or updating a Hospitality Device Model';
  return {
    Active: model.active,
    Manufacturer: model.manufacturer,
    Name: model.name,
    RecordingDevice: model.canDVR,
    ClientVersion: model.clientVersion,
    BacManufacturerCode: model.bacManufacturerCode,
    BacSuffix: model.bacSuffix,
    BacEthernetSuffix: model.bacEthernetSuffix,
    SNMPCommunity: model.snmpCommunity,
    ClientStoreValue: model.clientStoreValue,
    DeviceTypeID: model.deviceTypeID,
  };
};

const mapApiResDeviceModel = (
  model: CreateDeviceModel
): ApiResCreateDeviceModel => ({
  Model: model.name,
  ClientVersion: model.clientVersion,
  CanDVR: model.canDVR,
  System: model.system,
  Active: model.active,
  ClientStoreValue: model.clientStoreValue,
});

const mapBulkResponse = (
  response: ApiBulkResponse<ApiDeviceModel, ApiResCreateDeviceModel>,
  operatorType: OperatorType
): BulkDeviceModels => ({
  success: response.Success.map((deviceModel) =>
    mapDeviceModel(deviceModel, operatorType)
  ),
  fail: response.Fail.map((deviceModel) => ({
    active: deviceModel.Active,
    clientStoreValue: deviceModel.ClientStoreValue,
    clientVersion: deviceModel.ClientVersion,
    name: deviceModel.Model,
    canDVR: deviceModel.CanDVR,
    system: deviceModel.System,
  })) as CreateDeviceModel[],
});

const mapHosDeviceType = (deviceType: ApiDeviceType): DeviceType => ({
  id: deviceType.ID,
  system: deviceType.SystemType.Name as SystemType,
  name: deviceType.Name,
  active: deviceType.Active,
});

export const mapHosDeviceModel = (
  deviceModel: ApiDeviceModel
): DeviceModel => ({
  id: deviceModel.ID,
  active: deviceModel.Active,
  bacEthernetSuffix: deviceModel.BacEthernetSuffix,
  bacManufacturerCode: deviceModel.BacManufacturerCode,
  bacSuffix: deviceModel.BacSuffix,
  canDVR: deviceModel.CanDVR,
  clientStoreValue: deviceModel.ClientStoreValue,
  clientVersion: deviceModel.ClientVersion,
  deviceTypeID: deviceModel.DeviceType.ID,
  isSNMP: Boolean(deviceModel.RebootOID),
  manufacturer: deviceModel.Manufacturer,
  name: deviceModel.Name,
  rebootOID: deviceModel.RebootOID,
  snmpCommunity: deviceModel.SNMPCommunity,
  system: deviceModel.DeviceType.SystemType.Name,
  type: mapHosDeviceType(deviceModel.DeviceType),
});

export const mapResDeviceModel = (
  deviceModel: ApiDeviceModel
): DeviceModel => ({
  active: deviceModel.Active,
  clientStoreValue: deviceModel.ClientStoreValue,
  clientVersion: deviceModel.ClientVersion,
  name: deviceModel.Model ?? 'N/A',
  canDVR: deviceModel.CanDVR ?? false,
  system: deviceModel.System ?? 'N/A',
  type: deviceModel.System === 'MR' ? '' : null,
});

export const mapDeviceModel = (
  deviceModel: ApiDeviceModel,
  operatorType: OperatorType
): DeviceModel => {
  if (operatorType === 'Residential') return mapResDeviceModel(deviceModel);
  return mapHosDeviceModel(deviceModel);
};
